"use client";

import { type ReactNode, useEffect, useState, type FC, type Ref } from "react";
import { mergeClasses } from "@huub-web/utils";
import type { RecipeVariants } from "@vanilla-extract/recipes";

import { Edges } from "../Edges/Edges";
import { buttonStyle, containerStyle } from "./ButtonToggle.css";
import type { ValidatableProps } from "../../props";
import { elevations } from "../../theme/css/elevations.css";
import { focusVisibleInset } from "../../theme/css/focusVisible.css";
import { useFormValidation } from "../../hooks/useFormValidation";
import { Text } from "../Text/Text";

export type ButtonToggleValue = string | number;

export type ButtonToggleOption = {
  value: ButtonToggleValue;
  label?: ReactNode;
  trailing?: ReactNode;
  leading?: ReactNode;
  tooltip?: string;
};

export type ButtonToggleChangeHandler = (value: ButtonToggleValue) => void;

export type ButtonToggleProps = RecipeVariants<typeof buttonStyle> &
  RecipeVariants<typeof containerStyle> &
  ValidatableProps & {
    onChange?: ButtonToggleChangeHandler;
    options?: ButtonToggleOption[];
    disabled?: boolean;
    defaultValue?: ButtonToggleValue;
    value?: ButtonToggleValue;
    /**
     * Ref forwarded to the root element
     */
    ref?: Ref<HTMLDivElement>;
  };

/**
 * ButtonToggle component is a group of buttons that allows users to select a single option.
 */
export const ButtonToggle: FC<ButtonToggleProps> = ({
  id: idProp,
  disabled: disabledProp,
  error: errorProp,
  options = [],
  defaultValue,
  value: valueProp,
  size,
  alignSelf,
  name,
  validation,
  variant,
  onChange,
  ref,
  ...props
}) => {
  const handleOptionSelect = (option: ButtonToggleOption) => {
    onChange?.(option.value);
  };

  const [value, setValue] = useState<ButtonToggleValue | undefined>(
    valueProp ?? defaultValue,
  );

  const { id, error, disabled } = useFormValidation(value, {
    id: idProp,
    error: errorProp,
    disabled: disabledProp,
    validation,
    name,
  });

  useEffect(() => {
    setValue(valueProp);
  }, [valueProp]);

  return (
    <div
      ref={ref}
      className={containerStyle({ size, error, alignSelf })}
      {...(disabled ? { "aria-disabled": "true" } : {})}
      {...props}
    >
      {options?.map((option, i) => {
        const active = value === option.value;

        return (
          <Text
            as="button"
            key={i}
            onClick={() => handleOptionSelect(option)}
            className={mergeClasses(
              buttonStyle({ size, variant, active }),
              focusVisibleInset,
              active ? elevations.l : undefined,
            )}
            disabled={disabled}
            id={i === 0 ? id : undefined}
            type="button"
            {...(active ? { "data-active": "true" } : {})}
          >
            <Edges leading={option.leading} trailing={option.trailing}>
              {option.label}
            </Edges>
          </Text>
        );
      })}
    </div>
  );
};
